import { useTranslation } from "react-i18next"
import { useFormikContext } from "formik"

import { epCountries } from "../data"

export type Filing = 'first-filing' | 'pct-nationalization' | 'validation' | 'pct-validation' | 'subsequent-filing' | 'unset'

export type FilingFields = {
    countryCode: string
    firstFiling?: boolean
    pctRouteFiling?: boolean
    validated?: boolean
}

export function extractFiling(member: FilingFields): Filing {
    return member.firstFiling
        ? 'first-filing'
        : (member.validated && member.pctRouteFiling)
        ? 'pct-validation'
        : member.pctRouteFiling
        ? 'pct-nationalization'
        : member.validated
        ? 'validation'
        : member.pctRouteFiling === undefined && member.validated === undefined
        ? 'unset'
        : 'subsequent-filing'
}

export function filingToPartial(filing: Filing): Partial<FilingFields> {
    switch (filing) {
        case 'first-filing': return { firstFiling: true, pctRouteFiling: false, validated: false }
        case 'pct-nationalization': return { firstFiling: false, pctRouteFiling: true, validated: false }
        case 'validation': return { firstFiling: false, pctRouteFiling: false, validated: true }
        case 'pct-validation': return { firstFiling: false, pctRouteFiling: true, validated: true }
        case 'subsequent-filing': return { firstFiling: false, pctRouteFiling: false, validated: false }
        case 'unset': return { firstFiling: false, pctRouteFiling: undefined, validated: undefined }
    }
}

export function FilingSelection<T extends FilingFields>() {
    const { t } = useTranslation()
    const { values, setValues } = useFormikContext<T>()

    const filings: { filing: Filing; label: string; disabled?: boolean }[] = [
        { filing: 'unset', label: '-' },
        { filing: 'subsequent-filing', label: t('subsequent-filing') },
        { filing: 'first-filing', label: t('firstFiling') },
        { filing: 'pct-nationalization', label: t('pctRouteFiling') },
        { filing: 'validation', label: t('validated'), disabled: epCountries.indexOf(values.countryCode) < 0 },
        { filing: 'pct-validation', label: `${t('validated')} / ${t('pctRouteFiling')}`, disabled: epCountries.indexOf(values.countryCode) < 0 },
    ]

    return (
        <select
            className="form-select py-1 pl-2 h-8 w-full"
            value={extractFiling(values)}
            onChange={e => setValues({ ...values, ...filingToPartial(e.target.value as Filing) } as T)}
        >
            {filings.map(v => (
                <option key={v.filing} value={v.filing} disabled={v.disabled}>
                    {v.label}
                </option>
            ))}
        </select>
    )
}
